import Skeleton from '../Skeleton.jsx';

/** `w-full` so %-width children resolve against a real line width (avoids “narrow column” shrink). */
const BUBBLE_WIDTH = {
  /** Matches {@link ChatPanelConversationView} agent bubble. */
  default: 'w-full max-w-[min(100%,36rem)]',
  /** Full-panel loading overlay — wide bubbles. */
  overlay: 'w-full max-w-[min(100%,52rem)] min-w-[min(100%,18rem)]',
  /** Waiting for first tokens after send — wide, short block. */
  streaming: 'w-full max-w-[min(100%,48rem)] min-w-[min(100%,14rem)]',
};

/**
 * @param {object} props
 * @param {'full'|'compact'|'pending'} [props.variant='full'] — `pending`: short wide placeholder (streaming). `compact`: shorter overlay history.
 * @param {'default'|'overlay'|'streaming'} [props.widthMode='default'] — ignored when `variant === 'pending'`.
 */
const ChatPanelAssistantMessageSkeleton = ({ variant = 'full', widthMode = 'default' }) => {
  if (variant === 'pending') {
    return (
      <div className="flex w-full min-w-0 justify-start">
        <div
          className={`${BUBBLE_WIDTH.streaming} rounded-xl border border-edge bg-surface-muted px-4 py-2.5 dark:bg-surface-muted`}
        >
          <div className="flex flex-col gap-2">
            <Skeleton height={11} width="100%" borderRadius={4} className="max-w-full" />
            <Skeleton height={11} width="94%" borderRadius={4} className="max-w-full" />
            <Skeleton height={11} width="58%" borderRadius={4} className="max-w-full" />
          </div>
        </div>
      </div>
    );
  }

  const compact = variant === 'compact';
  const bubbleWidth = BUBBLE_WIDTH[widthMode] ?? BUBBLE_WIDTH.default;

  return (
    <div className="flex w-full min-w-0 justify-start">
      <div
        className={`${bubbleWidth} rounded-xl border border-edge bg-surface-muted px-4 py-3 dark:bg-surface-muted`}
      >
        <div className="flex flex-col gap-2.5">
          <Skeleton height={16} width={compact ? '42%' : '46%'} borderRadius={4} className="max-w-full" />
          <div className="flex flex-col gap-2 pl-0.5">
            <div className="flex items-start gap-2">
              <Skeleton height={6} width={6} borderRadius={9999} className="mt-1.5 shrink-0" />
              <Skeleton height={14} width={compact ? '88%' : '98%'} borderRadius={4} className="min-w-0 max-w-full" />
            </div>
            <div className="flex items-start gap-2">
              <Skeleton height={6} width={6} borderRadius={9999} className="mt-1.5 shrink-0" />
              <Skeleton height={14} width={compact ? '74%' : '92%'} borderRadius={4} className="max-w-full" />
            </div>
            {!compact ? (
              <div className="flex items-start gap-2">
                <Skeleton height={6} width={6} borderRadius={9999} className="mt-1.5 shrink-0" />
                <Skeleton height={14} width="80%" borderRadius={4} className="max-w-full" />
              </div>
            ) : null}
          </div>
          {!compact ? (
            <>
              <Skeleton height={14} width="100%" borderRadius={4} className="max-w-full" />
              <Skeleton height={14} width="94%" borderRadius={4} className="max-w-full" />
              <div className="flex flex-col gap-1.5 pt-1">
                <div className="flex items-start gap-2">
                  <Skeleton height={14} width={14} borderRadius={4} className="mt-0.5 shrink-0" />
                  <Skeleton height={14} width="78%" borderRadius={4} className="max-w-full" />
                </div>
                <div className="flex items-start gap-2">
                  <Skeleton height={14} width={14} borderRadius={4} className="mt-0.5 shrink-0" />
                  <Skeleton height={14} width="65%" borderRadius={4} className="max-w-full" />
                </div>
              </div>
            </>
          ) : (
            <Skeleton height={14} width="96%" borderRadius={4} className="max-w-full" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPanelAssistantMessageSkeleton;
